import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { ApiLink } from "../APILink/ApiLink";

export default function useAccountsGridData({
  headers,
  ledgerID,
  endpoint = "Accounts/getAccountsGrid",
  onError,
  initialPageSize = 10,
}) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [rowCount, setRowCount] = useState(0);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: initialPageSize,
  });
  const [sortModel, setSortModel] = useState([]);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [quickFilter, setQuickFilter] = useState([]);
  const [debouncedQuickFilter, setDebouncedQuickFilter] = useState([]);

  const headersRef = useRef(headers);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    headersRef.current = headers;
  }, [headers]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setDebouncedQuickFilter(quickFilter);
    }, 350);

    return () => clearTimeout(debounceTimer);
  }, [quickFilter]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(ApiLink.API_URL + endpoint, {
        headers: headersRef.current,
        params: {
          ledgerID,
          page: paginationModel.page,
          pageSize: paginationModel.pageSize,
          sort: JSON.stringify(sortModel),
          filter: JSON.stringify(filterModel.items),
          quickFilter: debouncedQuickFilter.join(" "),
        },
      });

      const responseData = response.data;
      const items = Array.isArray(responseData)
        ? responseData
        : Array.isArray(responseData?.items)
          ? responseData.items
          : [];
      const total = Array.isArray(responseData)
        ? responseData.length
        : Number.isFinite(responseData?.total)
          ? responseData.total
          : items.length;

      setData(items);
      setRowCount(total);
    } catch (error) {
      if (onErrorRef.current) {
        onErrorRef.current(error);
      }
    } finally {
      setLoading(false);
    }
  }, [ledgerID, endpoint, paginationModel, sortModel, filterModel, debouncedQuickFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    loading,
    data,
    rowCount,
    paginationModel,
    setPaginationModel,
    sortModel,
    setSortModel,
    filterModel,
    setFilterModel,
    quickFilter,
    setQuickFilter,
    fetchData,
  };
}
